import { useLanguage } from '../context/LanguageContext';
import { Menu, X, Globe, HelpCircle, Sparkles } from 'lucide-react';
import { useState } from 'react';
import FAQModal from './FAQModal';

// Logo image URL
const LOGO_URL = "/logo-transparent.png";

const Navigation = ({ onFAQClick, isFAQOpen, onCardGameClick, onBookCall }) => {
  const { t, language, toggleLanguage } = useLanguage();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navItems = [
    { key: 'nav_about', href: '#about' },
    { key: 'nav_method', href: '#satya' },
    { key: 'nav_services', href: '#services' },
    { key: 'nav_contact', href: '#contact' },
  ];

  const scrollToSection = (e, href) => {
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileMenuOpen(false);
  };

  const handleFAQClick = (e) => {
    e.preventDefault();
    setMobileMenuOpen(false);
    onFAQClick();
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-40 glass" data-testid="navigation" role="navigation" aria-label={language === 'he' ? 'ניווט ראשי' : 'Main navigation'}>
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between h-36 md:h-40">
          {/* Logo - Left side */}
          <a 
            href="#hero" 
            className="flex items-center"
            onClick={(e) => scrollToSection(e, '#hero')}
            data-testid="nav-logo"
          >
            <img 
              src={LOGO_URL}
              alt="Eran MH Coaching"
              className="h-48 md:h-60 w-auto"
              style={{ mixBlendMode: 'multiply' }}
            />
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navItems.map((item) => (
              <a
                key={item.key}
                href={item.href}
                onClick={(e) => scrollToSection(e, item.href)}
                className="text-sm font-medium text-gray-700 hover:text-sage-600 transition-colors"
                style={{ color: 'var(--charcoal)' }}
                data-testid={`nav-${item.key}`}
              >
                {t(item.key)}
              </a>
            ))}
            
            {/* FAQ Button */}
            <button
              onClick={handleFAQClick}
              className="flex items-center gap-1.5 text-sm font-medium transition-colors hover:text-sage-600"
              style={{ color: 'var(--charcoal)' }}
              data-testid="nav-faq"
            >
              <HelpCircle size={16} />
              <span>{language === 'he' ? 'שאלות נפוצות' : 'FAQ'}</span>
            </button>

            {/* Card Game Button */}
            <button
              onClick={() => { onCardGameClick && onCardGameClick(); }}
              className="flex items-center gap-1.5 text-sm font-medium transition-colors hover:text-sage-600"
              style={{ color: 'var(--sage-600)' }}
              data-testid="nav-card-game"
            >
              <Sparkles size={16} />
              <span>{language === 'he' ? 'שלוף קלף' : 'Pull a Card'}</span>
            </button>
            
            {/* Language Toggle */}
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-gray-200 rounded-full hover:bg-gray-50 transition-colors"
              data-testid="language-toggle"
            >
              <Globe size={16} />
              <span>{language === 'he' ? 'EN' : 'עב'}</span>
            </button>

            {/* CTA Button */}
            <button
              onClick={onBookCall}
              className="btn-primary text-sm py-2 px-5"
              data-testid="nav-book-btn"
            >
              {t('nav_book')}
            </button>
          </div>

          {/* Mobile Menu Button */}
          <div className="flex items-center gap-3 md:hidden">
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-1 px-2 py-1 text-sm border border-gray-200 rounded-full"
              data-testid="language-toggle-mobile"
            >
              <Globe size={14} />
              <span>{language === 'he' ? 'EN' : 'עב'}</span>
            </button>
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Toggle mobile menu"
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div 
            className="md:hidden py-4 border-t border-gray-100 animate-fade-in"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col gap-4">
              {navItems.map((item) => (
                <a
                  key={item.key}
                  href={item.href}
                  onClick={(e) => scrollToSection(e, item.href)}
                  className="text-base font-medium text-gray-700 hover:text-sage-600 transition-colors py-2"
                  data-testid={`mobile-nav-${item.key}`}
                >
                  {t(item.key)}
                </a>
              ))}
              
              {/* FAQ Button - Mobile */}
              <button
                onClick={handleFAQClick}
                className="flex items-center gap-2 text-base font-medium text-gray-700 hover:text-sage-600 transition-colors py-2"
                data-testid="mobile-nav-faq"
              >
                <HelpCircle size={18} />
                <span>{language === 'he' ? 'שאלות נפוצות' : 'FAQ'}</span>
              </button>

              {/* Card Game - Mobile */}
              <button
                onClick={() => { setMobileMenuOpen(false); onCardGameClick && onCardGameClick(); }}
                className="flex items-center gap-2 text-base font-medium transition-colors py-2"
                style={{ color: 'var(--sage-600)' }}
                data-testid="mobile-nav-card-game"
              >
                <Sparkles size={18} />
                <span>{language === 'he' ? 'שלוף קלף' : 'Pull a Card'}</span>
              </button>
              
              <button
                onClick={() => { setMobileMenuOpen(false); onBookCall && onBookCall(); }}
                className="btn-primary text-center mt-2"
                data-testid="mobile-nav-book-btn"
              >
                {t('nav_book')}
              </button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
